/**
 * Cart Rewards Progress Bar
 * Custom element that tracks cart total against 4 configurable reward thresholds.
 * Dynamically queries DOM nodes and fetches fresh cart data on load.
 */
class CartRewardsProgress extends HTMLElement {
  constructor() {
    super();
    this.moneyFormat = window.FoxTheme?.settings?.moneyFormat || '₹{{amount}}';
    this._tooltipTimer = null;
    this._desktopTooltipTimer = null;
    this._mobileSeqTimer = null;
    this._activeMobileMilestoneIndex = null;
    this._activeDesktopMilestoneIndex = null;
    this._lastAdjustedCart = 0;
    this._lastCartInput = 0;
    this._unlockedMilestones = new Set();
    this._isInitialLoad = true;
  }

  getMilestones() {
    const milestones = [];
    const defaultAmounts = [699, 1499, 1999, 2999];

    for (let i = 1; i <= 4; i++) {
      const amountAttr = this.getAttribute(`data-reward-${i}-amount`);
      const labelAttr = this.getAttribute(`data-reward-${i}-label`);
      const iconAttr = this.getAttribute(`data-reward-${i}-icon`);

      const cleanStr = (amountAttr || '').toString().replace(/[^0-9.]/g, '');
      let amount = parseFloat(cleanStr);
      if (isNaN(amount) || amount <= 0) {
        amount = defaultAmounts[i - 1];
      }

      const label = labelAttr || '';
      const icon = iconAttr || 'gift';

      milestones.push({ index: i, amount, label, icon });
    }
    milestones.sort((a, b) => a.amount - b.amount);
    return milestones;
  }

  connectedCallback() {
    this._isInitialLoad = true;
    const initialCartTotal = parseFloat(this.getAttribute('data-cart-total')) || 0;
    this.update(initialCartTotal);
    this.fetchLatestCart();

    // Trigger initial mobile landing popup only ONCE per session (first page visit only)
    requestAnimationFrame(() => {
      if (sessionStorage.getItem('crp_landing_shown')) return;

      if (!this.classList.contains('crp--show-tooltip')) {
        const currentCart = this._lastAdjustedCart || 0;
        const milestones = this.getMilestones();
        const nextTarget = milestones.find(m => currentCart < m.amount);
        if (nextTarget) {
          sessionStorage.setItem('crp_landing_shown', '1');

          this._suppressDocumentClickDismiss = true;
          if (this._suppressTimer) clearTimeout(this._suppressTimer);
          this._suppressTimer = setTimeout(() => {
            this._suppressDocumentClickDismiss = false;
          }, 2500);

          this.showMobileTooltipForMilestone(nextTarget.index, true, 7000);
        }
      }
    });

    const self = this;
    let fetchTimeout = null;
    const debouncedFetch = (delay = 200) => {
      if (fetchTimeout) clearTimeout(fetchTimeout);
      fetchTimeout = setTimeout(() => self.fetchLatestCart(), delay);
    };

    setTimeout(() => {
      this.fetchLatestCart();
      setTimeout(() => {
        this._isInitialLoad = false;
      }, 300);
    }, 200);

    // Click listener for tooltip close button
    this.addEventListener('click', (e) => {
      if (e.target.closest('[data-crp-tooltip-close]')) {
        e.stopPropagation();
        this.hideMobileTooltip();
        return;
      }
    });

    // Helper: true when a real pointer device (mouse/trackpad) is in use on a wide screen
    const isDesktopHover = () => window.matchMedia('(hover: hover) and (pointer: fine) and (min-width: 768px)').matches;

    // Click listener for mobile milestone circles (touch devices / narrow screens)
    this.addEventListener('click', (e) => {
      if (isDesktopHover()) return; // desktop uses hover instead
      const ms = e.target.closest('.crp-mobile__milestone');
      if (ms) {
        e.stopPropagation();
        const index = parseInt(ms.getAttribute('data-crp-mobile-milestone'), 10);
        if (index) {
          // Toggle off if clicking the threshold whose popup is currently open
          if (this.classList.contains('crp--show-tooltip') && this._activeMobileMilestoneIndex === index) {
            this.hideMobileTooltip();
            return;
          }

          if (this._mobileSeqTimer) clearTimeout(this._mobileSeqTimer);
          this.showMobileTooltipForMilestone(index, true, 7000);
          if (ms.classList.contains('crp-milestone--done')) {
            this.fireMilestoneConfetti(index);
          }
        }
      }
    });

    // Hover listeners for desktop milestone circles
    this._hoverLeaveTimer = null;

    this.querySelectorAll('.crp-mobile__milestone').forEach((ms) => {
      ms.addEventListener('mouseenter', () => {
        if (!isDesktopHover()) return;
        if (this._hoverLeaveTimer) { clearTimeout(this._hoverLeaveTimer); this._hoverLeaveTimer = null; }
        const index = parseInt(ms.getAttribute('data-crp-mobile-milestone'), 10);
        if (index) {
          if (this._mobileSeqTimer) clearTimeout(this._mobileSeqTimer);
          this.showMobileTooltipForMilestone(index, true, 0); // duration=0 means stay open while hovering
        }
      });

      ms.addEventListener('mouseleave', () => {
        if (!isDesktopHover()) return;
        this._hoverLeaveTimer = setTimeout(() => this.hideMobileTooltip(), 200);
      });
    });

    // Keep tooltip open when cursor moves into it (on desktop)
    const tooltip = this.querySelector('[data-crp-tooltip]');
    if (tooltip) {
      tooltip.addEventListener('mouseenter', () => {
        if (!isDesktopHover()) return;
        if (this._hoverLeaveTimer) { clearTimeout(this._hoverLeaveTimer); this._hoverLeaveTimer = null; }
      });
      tooltip.addEventListener('mouseleave', () => {
        if (!isDesktopHover()) return;
        this._hoverLeaveTimer = setTimeout(() => this.hideMobileTooltip(), 200);
      });
    }


    // Dismiss tooltips when clicking outside, but protect active celebration popups & ignore clicks on cart buttons
    document.addEventListener('click', (e) => {
      if (this._suppressDocumentClickDismiss) return;
      const isCartBtn = e.target.closest('button[name="add"], .product-form__submit, #gokwik-buy-now, .gokwik-checkout, [data-add-to-cart], .add-to-cart, .quick-add__submit');
      if (isCartBtn) return;

      if (!this.contains(e.target)) {
        this.hideMobileTooltip();
        this.hideDesktopTooltip();
      }
    });

    // Guarantee header cart icon opens cart drawer reliably every single time
    document.addEventListener('click', (e) => {
      const cartBtn = e.target.closest('.cart-drawer-button, [aria-controls="CartDrawer"], [aria-controls="cart-drawer"]');
      if (cartBtn) {
        const cartDrawer = document.querySelector('cart-drawer, #CartDrawer');
        if (cartDrawer) {
          // Clear cached overlay reference in FoxTheme ModalComponent to fix destroyed DOM node bug after cart refresh
          if ('_overlay' in cartDrawer) {
            cartDrawer._overlay = null;
          }
          cartDrawer.removeAttribute('inert');
          
          if (typeof cartDrawer.show === 'function') {
            cartDrawer.show(cartBtn);
          } else {
            cartDrawer.setAttribute('open', '');
          }
        }
      }
    });

    // Desktop milestone click listener removed — using mobile layout on all screens

    const handleCartUpdate = (cartValue) => {
      if (typeof cartValue !== 'undefined' && cartValue !== null && !isNaN(cartValue)) {
        this.update(cartValue);
      } else {
        debouncedFetch(100);
      }
    };

    // 1. Listen for DOM custom events & GoKwik events
    const onDomEvent = (event) => {
      const cart = event.detail?.cart || event.detail;
      const cartValue = cart?.items_subtotal_price ?? cart?.total_price ?? cart?.original_total_price;
      handleCartUpdate(cartValue);
    };

    [
      'cart:updated', 'cart:refresh', 'cart:change', 'cart:JSONReady', 'cart:add', 'cart:item-added',
      'gokwikLoaded', 'gokwikCartUpdated', 'gokwik:cart:updated', 'gkCartUpdated', 'gk:cart:updated'
    ].forEach(evtName => {
      document.addEventListener(evtName, onDomEvent);
      window.addEventListener(evtName, onDomEvent);
      document.documentElement.addEventListener(evtName, onDomEvent);
    });

    // 2. Subscribe to FoxTheme.pubsub
    if (window.FoxTheme && window.FoxTheme.pubsub && window.FoxTheme.pubsub.subscribe) {
      try {
        window.FoxTheme.pubsub.subscribe(window.FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, (data) => {
          const cartValue = data?.cart?.items_subtotal_price ?? data?.cart?.total_price;
          handleCartUpdate(cartValue);
        });
      } catch (e) {}
    }

    // 3. Intercept XMLHttpRequest for AJAX cart updates
    if (!window._crpXhrIntercepted) {
      window._crpXhrIntercepted = true;
      const origOpen = XMLHttpRequest.prototype.open;
      const origSend = XMLHttpRequest.prototype.send;

      XMLHttpRequest.prototype.open = function(method, url, ...rest) {
        this._crpUrl = typeof url === 'string' ? url : '';
        return origOpen.apply(this, [method, url, ...rest]);
      };

      XMLHttpRequest.prototype.send = function(...args) {
        this.addEventListener('load', () => {
          if (this._crpUrl && (this._crpUrl.includes('/cart/add') || this._crpUrl.includes('/cart/change') || this._crpUrl.includes('/cart/update') || this._crpUrl.includes('/cart/clear') || this._crpUrl.includes('gokwik'))) {
            debouncedFetch(200);
          }
        });
        return origSend.apply(this, args);
      };
    }

    // 4. Intercept fetch API calls to cart modification endpoints
    if (!window._crpFetchIntercepted) {
      window._crpFetchIntercepted = true;
      const originalFetch = window.fetch;
      window.fetch = function(...args) {
        return originalFetch.apply(this, args).then(response => {
          try {
            const url = typeof args[0] === 'string' ? args[0] : (args[0] && args[0].url ? args[0].url : '');
            if (typeof url === 'string' && (url.includes('/cart/add') || url.includes('/cart/change') || url.includes('/cart/update') || url.includes('/cart/clear') || url.includes('gokwik'))) {
              if (response && typeof response.clone === 'function') {
                response.clone().json().then(data => {
                  const cartVal = data?.items_subtotal_price ?? data?.total_price ?? data?.cart?.items_subtotal_price ?? data?.cart?.total_price;
                  if (typeof cartVal !== 'undefined' && cartVal !== null && !isNaN(cartVal)) {
                    self.update(cartVal);
                  } else {
                    debouncedFetch(250);
                  }
                }).catch(() => debouncedFetch(250));
              } else {
                debouncedFetch(250);
              }
            }
          } catch (e) {
            // Silently ignore interceptor error so original fetch response is never impacted
          }
          return response;
        });
      };
    }

    // 5. Lightweight click listener for Add To Cart buttons
    document.addEventListener('click', (e) => {
      const target = e.target;
      if (!target) return;
      const isCartBtn = target.closest('button[name="add"], .product-form__submit, #gokwik-buy-now, .gokwik-checkout, [data-add-to-cart], .add-to-cart, .quick-add__submit');
      if (isCartBtn) {
        debouncedFetch(400);
      }
    }, { passive: true });

  }

  fetchLatestCart() {
    if (this._isFetchingCart) return;
    this._isFetchingCart = true;

    const root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root ? window.Shopify.routes.root : '/';
    fetch(root + 'cart.js')
      .then(res => res.json())
      .then(cart => {
        this._isFetchingCart = false;
        if (cart && (typeof cart.total_price !== 'undefined' || typeof cart.items_subtotal_price !== 'undefined')) {
          const total = typeof cart.items_subtotal_price !== 'undefined' ? cart.items_subtotal_price : cart.total_price;
          this.update(total);
        }
      })
      .catch(() => {
        this._isFetchingCart = false;
      });
  }

  parseCartAmount(cartInput) {
    let raw = Number(cartInput) || 0;
    if (raw <= 0) return 0;

    const milestones = this.getMilestones();
    const firstAmount = milestones[0]?.amount || 699;

    // Standard Shopify cart totals are passed in cents/paise (e.g. 69900 for ₹699, 149900 for ₹1499)
    if (raw >= firstAmount * 8 || (raw >= 1000 && raw % 100 === 0)) {
      return raw / 100;
    }
    return raw;
  }

  /**
   * Core update method
   * @param {number} cartInput - Cart total in cents or base currency units
   */
  update(cartInput) {
    let cartAmount = this.parseCartAmount(cartInput);
    this._lastCartInput = Number(cartInput) || 0;
    const milestones = this.getMilestones();

    const currencyRate = Number(window.Shopify?.currency?.rate || 1);
    const adjustedCart = cartAmount * currencyRate;
    this._lastAdjustedCart = adjustedCart;

    // Ensure component is visible
    this.style.display = '';
    document.body.classList.add('crp-body-offset');

    // Dynamic DOM references
    const desktopFill = this.querySelector('[data-crp-fill]');
    const mobileFill = this.querySelector('[data-crp-mobile-fill]');
    const remainingEl = this.querySelector('[data-crp-remaining]');
    const nextLabelEl = this.querySelector('[data-crp-next-label]');

    // Precise segmented progress calculations:
    // Desktop milestone center markers: 20%, 45%, 70%, 95% (radius = 1.8%)
    // Mobile milestone center markers: 0%, 33.33%, 66.67%, 100% (radius = 5.7%)
    let desktopProgress = 0;
    let mobileProgress = 0;

    if (milestones.length >= 4) {
      const m1 = milestones[0].amount;
      const m2 = milestones[1].amount;
      const m3 = milestones[2].amount;
      const m4 = milestones[3].amount;

      if (adjustedCart <= 0) {
        desktopProgress = 0;
        mobileProgress = 0;
      } else if (adjustedCart < m1) {
        const seg = adjustedCart / m1;
        desktopProgress = seg * 18.2;
        mobileProgress = seg * 4.0;
      } else if (adjustedCart < m2) {
        const seg = (adjustedCart - m1) / (m2 - m1);
        desktopProgress = 20 + seg * 23.2;
        mobileProgress = 5.7 + seg * 22.0; // Starts from bottom edge of circle 1 (5.7%) up to top edge of circle 2 (27.7%)
      } else if (adjustedCart < m3) {
        const seg = (adjustedCart - m2) / (m3 - m2);
        desktopProgress = 45 + seg * 23.2;
        mobileProgress = 39.0 + seg * 22.4; // Starts from bottom edge of circle 2 (39.0%) up to top edge of circle 3 (61.4%)
      } else if (adjustedCart < m4) {
        const seg = (adjustedCart - m3) / (m4 - m3);
        desktopProgress = 70 + seg * 23.2;
        mobileProgress = 72.4 + seg * 21.9; // Starts from bottom edge of circle 3 (72.4%) up to top edge of circle 4 (94.3%)
      } else {
        desktopProgress = 100;
        mobileProgress = 100;
      }

      if (adjustedCart >= m1 && adjustedCart < m2) {
        if (mobileProgress < 5.7) mobileProgress = 5.7;
        if (desktopProgress < 20) desktopProgress = 20;
      }
      if (adjustedCart >= m2) {
        if (desktopProgress < 45) desktopProgress = 45;
        if (mobileProgress < 33.33) mobileProgress = 33.33;
      }
      if (adjustedCart >= m3) {
        if (desktopProgress < 70) desktopProgress = 70;
        if (mobileProgress < 66.67) mobileProgress = 66.67;
      }
      if (adjustedCart >= m4) {
        desktopProgress = 100;
        mobileProgress = 100;
      }
    }

    desktopProgress = Math.max(0, Math.min(100, desktopProgress));
    mobileProgress = Math.max(0, Math.min(100, mobileProgress));

    // Update track fill - desktop (prominent black fill line)
    if (desktopFill) {
      desktopFill.style.setProperty('width', `${desktopProgress}%`, 'important');
      desktopFill.style.setProperty('background', '#000000', 'important');
      desktopFill.style.setProperty('background-color', '#000000', 'important');
      desktopFill.style.setProperty('display', 'block', 'important');
      desktopFill.style.setProperty('opacity', '1', 'important');
      desktopFill.style.setProperty('z-index', '10', 'important');
    }

    // Update track fill - mobile
    if (mobileFill) {
      mobileFill.style.setProperty('height', `${mobileProgress}%`, 'important');
      mobileFill.style.setProperty('background', '#000000', 'important');
      mobileFill.style.setProperty('background-color', '#000000', 'important');
      mobileFill.style.setProperty('display', 'block', 'important');
      mobileFill.style.setProperty('opacity', '1', 'important');
      mobileFill.style.setProperty('z-index', '10', 'important');
    }

    // Determine milestone states & track newly cleared milestones
    let nextMilestone = null;
    let allDone = milestones.length > 0;
    const newlyClearedIndexes = [];

    milestones.forEach((milestone) => {
      const isDone = adjustedCart >= milestone.amount;
      const wasDone = this._unlockedMilestones.has(milestone.index);
      const desktopMs = this.querySelector(`[data-crp-milestone="${milestone.index}"]`);
      const mobileMs = this.querySelector(`[data-crp-mobile-milestone="${milestone.index}"]`);

      if (isDone) {
        this._unlockedMilestones.add(milestone.index);
        if (!wasDone && !this._isInitialLoad) {
          newlyClearedIndexes.push(milestone.index);
        }

        if (desktopMs) {
          desktopMs.classList.add('crp-milestone--done');
          desktopMs.classList.remove('crp-milestone--active');
        }
        if (mobileMs) {
          mobileMs.classList.add('crp-milestone--done');
          mobileMs.classList.remove('crp-milestone--active');
        }
      } else {
        this._unlockedMilestones.delete(milestone.index);
        allDone = false;
        if (desktopMs) {
          desktopMs.classList.remove('crp-milestone--done', 'crp-milestone--active');
        }
        if (mobileMs) {
          mobileMs.classList.remove('crp-milestone--done', 'crp-milestone--active');
        }

        if (!nextMilestone) {
          nextMilestone = milestone;
          if (desktopMs) desktopMs.classList.add('crp-milestone--active');
          if (mobileMs) mobileMs.classList.add('crp-milestone--active');
        }
      }
    });

    if (this._isInitialLoad) {
      this._isInitialLoad = false;
    }

    // Toggle all-done state
    if (allDone) {
      this.classList.add('crp--all-done');
    } else {
      this.classList.remove('crp--all-done');
    }

    // Update desktop header message
    const desktopHeaderMsg = this.querySelector('[data-crp-desktop-message-header]');
    if (desktopHeaderMsg) {
      if (allDone) {
        desktopHeaderMsg.innerHTML = `🎉 Congratulations! You've unlocked all rewards!`;
      } else if (nextMilestone) {
        const remainingCents = Math.round((nextMilestone.amount - adjustedCart) * 100);
        const formattedRemaining = this.formatMoney(remainingCents);
        desktopHeaderMsg.innerHTML = `Add ${formattedRemaining} & unlock ${nextMilestone.label}`;
      }
    }

    // Automatically trigger popups and confetti celebration ONLY if milestone threshold(s) were freshly cleared!
    if (newlyClearedIndexes.length > 0 && !this._isInitialLoad) {
      this._suppressDocumentClickDismiss = true;
      if (this._suppressTimer) clearTimeout(this._suppressTimer);
      this._suppressTimer = setTimeout(() => {
        this._suppressDocumentClickDismiss = false;
      }, 3000);

      if (this._mobileSeqTimer) clearTimeout(this._mobileSeqTimer);

      newlyClearedIndexes.forEach(idx => {
        this.fireMilestoneConfetti(idx);
        this.showDesktopTooltipForMilestone(idx, true);
      });

      // Mobile Sequential Popup Flow:
      // Step 1: Show the Unlocked celebration popup on cleared milestone for 3.5 seconds
      const highestClearedIdx = Math.max(...newlyClearedIndexes);
      this.showMobileTooltipForMilestone(highestClearedIdx, true, 3500);

      // Step 2: After 3.5s (when unlocked popup disappears), open popup for NEXT target threshold beside its circle for 7 seconds
      {
        this._mobileSeqTimer = setTimeout(() => {
          const currentCart = this._lastAdjustedCart || adjustedCart;
          const currentMilestones = this.getMilestones();
          const nextTarget = currentMilestones.find(m => currentCart < m.amount);
          if (nextTarget) {
            this.showMobileTooltipForMilestone(nextTarget.index, true, 7000);
          }
        }, 3600);
      }
    }
  }

  fireMilestoneConfetti(milestoneIndex) {
    // Always use mobile icon since vertical capsule is shown on all screen sizes
    const mobileIcon = this.querySelector(`.crp-mobile [data-crp-mobile-milestone="${milestoneIndex}"] .crp-mobile__milestone-icon`);
    const target = mobileIcon;
    if (!target) return;

    const rect = target.getBoundingClientRect();
    if (rect.width === 0 && rect.height === 0) return;

    // Trigger ring flash aura animation on milestone circle
    target.classList.remove('crp-milestone-flash-active');
    void target.offsetWidth;
    target.classList.add('crp-milestone-flash-active');

    // Calculate normalized screen coordinates (0.0 to 1.0)
    const originX = (rect.left + rect.width / 2) / window.innerWidth;
    const originY = (rect.top + rect.height / 2) / window.innerHeight;

    this.launchCanvasConfetti(originX, originY);
  }

  launchCanvasConfetti(x, y) {
    let canvas = document.getElementById('crp-confetti-canvas');
    if (!canvas) {
      canvas = document.createElement('canvas');
      canvas.id = 'crp-confetti-canvas';
      canvas.style.cssText = 'position:fixed;top:0;left:0;width:100vw;height:100vh;pointer-events:none;z-index:999999;';
      document.body.appendChild(canvas);
    }

    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const colors = ['#f59e0b', '#10b981', '#ef4444', '#3b82f6', '#8b5cf6', '#ec4899', '#facc15', '#06b6d4', '#ffffff', '#ff5722'];
    const particles = [];
    const particleCount = 65;

    const startX = x * canvas.width;
    const startY = y * canvas.height;

    for (let i = 0; i < particleCount; i++) {
      const angle = (Math.PI * 2 * i) / particleCount + (Math.random() * 0.4 - 0.2);
      const speed = Math.random() * 9 + 4;
      particles.push({
        x: startX,
        y: startY,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed - 2.5,
        color: colors[i % colors.length],
        size: Math.random() * 7 + 6,
        rotation: Math.random() * Math.PI * 2,
        rotationSpeed: (Math.random() - 0.5) * 0.2,
        opacity: 1,
        life: 0,
        maxLife: Math.random() * 25 + 55
      });
    }

    let animationId = null;
    const render = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      let aliveCount = 0;

      particles.forEach(p => {
        if (p.opacity <= 0) return;
        aliveCount++;

        p.x += p.vx;
        p.y += p.vy;
        p.vy += 0.18; // gravity
        p.vx *= 0.98; // air drag
        p.rotation += p.rotationSpeed;
        p.life++;

        if (p.life > p.maxLife - 20) {
          p.opacity -= 0.05;
        }
        if (p.opacity < 0) p.opacity = 0;

        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rotation);
        ctx.globalAlpha = Math.max(0, p.opacity);
        ctx.fillStyle = p.color;

        // Draw confetti shape (rectangles & squares)
        ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size * 0.7);
        ctx.restore();
      });

      if (aliveCount > 0) {
        animationId = requestAnimationFrame(render);
      } else {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        if (animationId) cancelAnimationFrame(animationId);
      }
    };

    render();
  }

  showDesktopTooltipForMilestone(milestoneIndex, triggerShow = true) {
    this._activeDesktopMilestoneIndex = milestoneIndex;
    const milestones = this.getMilestones();
    const milestone = milestones.find(m => m.index === milestoneIndex) || milestones[0];
    if (!milestone) return;

    const tooltip = this.querySelector('[data-crp-desktop-tooltip]');
    const desktopMsgEl = this.querySelector('[data-crp-desktop-message]');
    const desktopSubtextEl = this.querySelector('[data-crp-desktop-subtext]');
    const desktopMs = this.querySelector(`[data-crp-milestone="${milestoneIndex}"]`);

    const adjustedCart = this._lastAdjustedCart || 0;
    const isDone = adjustedCart >= milestone.amount;

    if (isDone) {
      if (desktopMsgEl) {
        desktopMsgEl.textContent = `✓ ${milestone.label} Unlocked!`;
      }
    } else {
      const remainingCents = Math.round((milestone.amount - adjustedCart) * 100);
      const formattedRemaining = this.formatMoney(remainingCents);

      if (desktopMsgEl) {
        desktopMsgEl.textContent = `Add ${formattedRemaining} & unlock ${milestone.label}`;
      }
    }
    if (desktopSubtextEl) {
      desktopSubtextEl.textContent = '';
      desktopSubtextEl.style.display = 'none';
    }

    // Position tooltip horizontally aligned with the milestone circle
    if (tooltip && desktopMs) {
      const wrapper = this.querySelector('.crp-desktop__track-wrapper');
      if (wrapper) {
        const msRect = desktopMs.getBoundingClientRect();
        const wrapperRect = wrapper.getBoundingClientRect();
        const leftOffset = msRect.left - wrapperRect.left + (msRect.width / 2);
        tooltip.style.left = `${leftOffset}px`;
      }
    }

    if (triggerShow) {
      this.showDesktopTooltip();
    }
  }

  showDesktopTooltip() {
    this.classList.add('crp--show-desktop-tooltip');

    if (this._desktopTooltipTimer) clearTimeout(this._desktopTooltipTimer);
    this._desktopTooltipTimer = setTimeout(() => {
      this.classList.remove('crp--show-desktop-tooltip');
      this._activeDesktopMilestoneIndex = null;
    }, 4000);
  }

  hideDesktopTooltip() {
    if (this._desktopTooltipTimer) clearTimeout(this._desktopTooltipTimer);
    this.classList.remove('crp--show-desktop-tooltip');
    this._activeDesktopMilestoneIndex = null;
  }

  showMobileTooltipForMilestone(milestoneIndex, triggerShow = true, duration = 7000) {
    this._activeMobileMilestoneIndex = milestoneIndex;
    const milestones = this.getMilestones();
    const milestone = milestones.find(m => m.index === milestoneIndex) || milestones[0];
    if (!milestone) return;

    const tooltip = this.querySelector('[data-crp-tooltip]');
    const mobileMsgEl = this.querySelector('[data-crp-mobile-message]');
    const mobileSubtextEl = this.querySelector('[data-crp-mobile-subtext]');
    const mobileMs = this.querySelector(`[data-crp-mobile-milestone="${milestoneIndex}"]`);

    const adjustedCart = this._lastAdjustedCart || 0;
    const isDone = adjustedCart >= milestone.amount;

    if (isDone) {
      if (mobileMsgEl) {
        mobileMsgEl.textContent = `✓ ${milestone.label}`;
      }
      if (mobileSubtextEl) {
        mobileSubtextEl.textContent = 'Unlocked!';
        mobileSubtextEl.style.display = '';
      }
    } else {
      const remainingCents = Math.round((milestone.amount - adjustedCart) * 100);
      const formattedRemaining = this.formatMoney(remainingCents);
      const formattedCartTotal = this.formatMoney(Math.round(adjustedCart * 100));

      if (mobileMsgEl) {
        mobileMsgEl.textContent = `Add ${formattedRemaining} & unlock ${milestone.label}`;
      }
      if (mobileSubtextEl) {
        mobileSubtextEl.textContent = `Current cart value: ${formattedCartTotal}`;
        mobileSubtextEl.style.display = '';
      }
    }

    // Position tooltip vertically aligned with the clicked milestone circle
    if (tooltip && mobileMs) {
      const mobileContainer = this.querySelector('.crp-mobile');
      if (mobileContainer) {
        const msRect = mobileMs.getBoundingClientRect();
        const containerRect = mobileContainer.getBoundingClientRect();
        let topOffset = msRect.top - containerRect.top + (msRect.height / 2);
        if (isNaN(topOffset) || topOffset <= 0 || msRect.height === 0) {
          topOffset = 24 + (milestoneIndex - 1) * 70;
        }
        tooltip.style.top = `${topOffset}px`;
      }
    }

    if (triggerShow) {
      this.showMobileTooltip(duration);
    }
  }

  formatMoney(cents) {
    if (typeof FoxTheme !== 'undefined' && FoxTheme.Currency && FoxTheme.Currency.formatMoney) {
      return FoxTheme.Currency.formatMoney(cents, this.moneyFormat);
    }
    const amount = (cents / 100).toFixed(2);
    return this.moneyFormat.replace('{{amount}}', amount).replace('{{amount_no_decimals}}', Math.round(cents / 100));
  }

  showMobileTooltip(duration = 7000) {
    this.classList.add('crp--show-tooltip');

    if (this._tooltipTimer) clearTimeout(this._tooltipTimer);
    // duration === 0 means hover-controlled (desktop) — no auto-dismiss timer
    if (duration > 0) {
      this._tooltipTimer = setTimeout(() => {
        this.classList.remove('crp--show-tooltip');
        this._activeMobileMilestoneIndex = null;
      }, duration);
    }
  }

  hideMobileTooltip() {
    if (this._tooltipTimer) clearTimeout(this._tooltipTimer);
    if (this._mobileSeqTimer) clearTimeout(this._mobileSeqTimer);
    this.classList.remove('crp--show-tooltip');
    this._activeMobileMilestoneIndex = null;
  }
}

customElements.define('cart-rewards-progress', CartRewardsProgress);
